import type { WalletTransactionResponseDto } from "@/types/wallet.dto";
import type { ReferralEarningResponseDto, ReferralNetworkDto } from "@/types/referral.dto";
import type { SettlementResponseDto } from "@/types/wallet.dto";
import type { AccountReferralRow, AccountReferralEarningRow, AccountWalletTransactionRow } from "@/types/account.ui";
import { SettlementStatus, TransactionCategory, TransactionType } from "@/types/enums";
import { formatOrderDate } from "./order-mappers";

const transactionCategoryMeta: Record<TransactionCategory, { key: string; label: string }> = {
  [TransactionCategory.OrderPayment]: { key: "order_payment", label: "Order Payment" },
  [TransactionCategory.OrderRefund]: { key: "order_refund", label: "Refund" },
  [TransactionCategory.ReferralCommission]: { key: "referral_commission", label: "Referral" },
  [TransactionCategory.DeliveryCommission]: { key: "delivery_commission", label: "Delivery Commission" },
  [TransactionCategory.StoreSettlement]: { key: "store_settlement", label: "Store Settlement" },
  [TransactionCategory.Withdrawal]: { key: "withdrawal", label: "Withdrawal" },
  [TransactionCategory.AdminCommission]: { key: "admin_commission", label: "Admin Commission" },
  [TransactionCategory.WalletTopUp]: { key: "wallet_top_up", label: "Wallet Top-up" },
  [TransactionCategory.CashbackReward]: { key: "cashback_reward", label: "Cashback" },
  [TransactionCategory.P2PTransferOut]: { key: "p2ptransfer_out", label: "P2P Out" },
  [TransactionCategory.P2PTransferIn]: { key: "p2ptransfer_in", label: "P2P In" },
  [TransactionCategory.RewardPoolBonus]: { key: "reward_pool_bonus", label: "Reward Pool Bonus" },
  [TransactionCategory.SelfCashback]: { key: "self_cashback", label: "Self Cashback" },
  [TransactionCategory.LevelCashback]: { key: "level_cashback", label: "Level Cashback" },
  [TransactionCategory.RewardCashback]: { key: "reward_cashback", label: "Reward" },
  [TransactionCategory.IncomeReversal]: { key: "income_reversal", label: "Income Reversal" },
  [TransactionCategory.AdminAdjustment]: { key: "admin_adjustment", label: "Admin Adjustment" },
};

function fallbackCategoryKey(categoryText: string): string {
  return categoryText
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .replace(/\s+/g, "_");
}

export function mapWalletTransactionToRow(
  dto: WalletTransactionResponseDto,
): AccountWalletTransactionRow {
  const categoryMeta = transactionCategoryMeta[dto.category];

  return {
    id: String(dto.id),
    type: categoryMeta?.key ?? fallbackCategoryKey(dto.categoryText),
    categoryLabel: categoryMeta?.label ?? dto.categoryText,
    description: dto.description,
    amount: dto.amount,
    date: formatOrderDate(dto.createdAt),
    isCredit: dto.type === TransactionType.Credit,
  };
}

export function flattenReferralNetwork(
  rootOrNodes: ReferralNetworkDto | ReferralNetworkDto[] | null | undefined,
  acc: AccountReferralRow[] = [],
): AccountReferralRow[] {
  if (!rootOrNodes) return acc;

  const nodes = Array.isArray(rootOrNodes)
    ? rootOrNodes
    : rootOrNodes.level === 0
      ? (rootOrNodes.children ?? [])
      : [rootOrNodes];

  for (const node of nodes) {
    if (node.level > 0) {
      acc.push({
        id: node.userId,
        name: node.userName || "User",
        joinedDate: formatOrderDate(node.joinedAt),
        totalOrders: node.hasMadePurchase ? 1 : 0,
        commissionEarned: Number(node.commissionGenerated ?? 0),
        level: node.level,
      });
    }
    if (node.children?.length) {
      flattenReferralNetwork(node.children, acc);
    }
  }
  return acc;
}

export function mapWalletTransactionsToRows(
  items: WalletTransactionResponseDto[],
): AccountWalletTransactionRow[] {
  return items.map(mapWalletTransactionToRow);
}

export function mapReferralEarningToRow(dto: ReferralEarningResponseDto): AccountReferralEarningRow {
  return {
    id: String(dto.id),
    sourceUserName: dto.sourceUserName,
    orderNumber: dto.orderNumber,
    level: dto.level,
    orderAmount: dto.orderAmount,
    commissionPercentage: dto.commissionPercentage,
    earnedAmount: dto.earnedAmount,
    isCredited: dto.isCredited,
    createdAt: formatOrderDate(dto.createdAt),
  };
}

export function mapReferralEarningsToRows(items: ReferralEarningResponseDto[]): AccountReferralEarningRow[] {
  return items.map(mapReferralEarningToRow);
}

export interface AdminSettlementRow {
  id: string;
  settlementId: string;
  recipient: string;
  type: "store" | "delivery" | "unknown";
  amount: number;
  period: string;
  status: "pending" | "processing" | "completed" | "failed";
  date: string;
}

export function mapSettlementToAdminRow(dto: SettlementResponseDto, ownerType?: string): AdminSettlementRow {
  const statusMap: Record<SettlementStatus, AdminSettlementRow["status"]> = {
    [SettlementStatus.Pending]: "pending",
    [SettlementStatus.Processing]: "processing",
    [SettlementStatus.Completed]: "completed",
    [SettlementStatus.Failed]: "failed",
  };

  const type =
    ownerType === "Store" || ownerType === "1"
      ? "store"
      : ownerType === "DeliveryPartner" || ownerType === "2"
        ? "delivery"
        : "unknown";

  return {
    id: String(dto.id),
    settlementId: dto.settlementId,
    recipient: dto.settlementId,
    type,
    amount: dto.amount,
    period: `${formatOrderDate(dto.periodStart)} – ${formatOrderDate(dto.periodEnd)}`,
    status: statusMap[dto.status] ?? "pending",
    date: formatOrderDate(dto.createdAt),
  };
}

export function mapSettlementsToAdminRows(items: SettlementResponseDto[]): AdminSettlementRow[] {
  return items.map((item) => mapSettlementToAdminRow(item));
}
